import { useEffect, useState } from 'react'
import { Button, Typography, Chip } from 'amo-styling-library'
import { DataGrid } from '@mui/x-data-grid'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { ButtonSize, GlobalRadius, Border } from 'amo-styling-library'
import { SimpleDashboardLayout } from '../components/layout/SimpleDashboardLayout'
import { ingestionService } from '../services/tripService'
import type { SyncLogEntry, SyncResult, StatsResponse } from '../types/trip'

// ============================================================================
// HELPERS
// ============================================================================

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString('nl-BE')
}

function getSyncStatusChip(status: SyncLogEntry['status']) {
  switch (status) {
    case 'completed':
      return <Chip label="Voltooid" status="success" />
    case 'failed':
      return <Chip label="Mislukt" status="error" />
    case 'processing':
      return <Chip label="Bezig" status="info" />
    case 'pending':
    default:
      return <Chip label="In wachtrij" status="warning" />
  }
}

// ============================================================================
// STAT CARD
// ============================================================================

interface StatCardProps {
  label: string
  value: number | string
  sublabel?: string
}

function StatCard({ label, value, sublabel }: StatCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-1">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sublabel && <p className="text-xs text-gray-400">{sublabel}</p>}
    </div>
  )
}

// ============================================================================
// PAGE
// ============================================================================

function SyncDashboardPage() {
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [recentEntries, setRecentEntries] = useState<SyncLogEntry[]>([])
  const [loadingStats, setLoadingStats] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  const fetchStats = async () => {
    setLoadingStats(true)
    try {
      const result = await ingestionService.getStats()
      setStats(result)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    } finally {
      setLoadingStats(false)
    }
  }

  const fetchHistory = async () => {
    setLoadingHistory(true)
    try {
      const result = await ingestionService.getSyncStatus()
      setRecentEntries(result.recentEntries)
    } catch (err) {
      console.error('Failed to fetch sync history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleRefresh = () => {
    fetchStats()
    fetchHistory()
  }

  useEffect(() => {
    fetchStats()
    fetchHistory()
  }, [])

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)
    setSyncError(null)

    try {
      const result = await ingestionService.triggerSync()
      setSyncResult(result)
      await Promise.all([fetchStats(), fetchHistory()])
    } catch (err) {
      console.error('Sync failed:', err)
      setSyncError('Synchronisatie mislukt. Controleer de server logs.')
    } finally {
      setSyncing(false)
    }
  }

  // DataGrid columns for recent sync history
  const historyColumns: GridColDef[] = [
    {
      field: 'blobName',
      headerName: 'Bestandsnaam',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fileType',
      headerName: 'Type',
      width: 120,
      renderCell: (params: GridRenderCellParams) => params.value || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams) =>
        getSyncStatusChip(params.value as SyncLogEntry['status']),
    },
    {
      field: 'recordsProcessed',
      headerName: 'Verwerkt',
      width: 100,
      type: 'number',
    },
    {
      field: 'recordsInserted',
      headerName: 'Nieuw',
      width: 90,
      type: 'number',
    },
    {
      field: 'recordsUpdated',
      headerName: 'Bijgewerkt',
      width: 110,
      type: 'number',
    },
    {
      field: 'recordsErrored',
      headerName: 'Fouten',
      width: 90,
      type: 'number',
    },
    {
      field: 'startedAt',
      headerName: 'Gestart',
      width: 170,
      renderCell: (params: GridRenderCellParams) =>
        formatDateTime(params.value as string | null),
    },
    {
      field: 'completedAt',
      headerName: 'Voltooid',
      width: 170,
      renderCell: (params: GridRenderCellParams) =>
        formatDateTime(params.value as string | null),
    },
  ]

  // Give each row a stable id for the DataGrid
  const historyRows = recentEntries.map((entry, idx) => ({
    ...entry,
    _rowId: entry.id ?? `row-${idx}`,
  }))

  return (
    <SimpleDashboardLayout>
      <div className="space-y-6">
        {/* Stats cards */}
        <div className="bg-white rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography variant="h2" className="text-gray-900">
              Synchronisatie Dashboard
            </Typography>
            <Button
              label="Ververs"
              size={ButtonSize.MEDIUM}
              radius={GlobalRadius.ROUNDED}
              border={Border.BORDER}
              onClick={handleRefresh}
            />
          </div>

          {loadingStats ? (
            <p className="text-gray-600">Laden...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                label="Totaal Ritten"
                value={stats?.totalTrips ?? '-'}
              />
              <StatCard
                label="Totaal Activiteiten"
                value={stats?.totalActivities ?? '-'}
              />
              <StatCard
                label="Activiteittypes"
                value={stats?.totalActivityTypes ?? '-'}
              />
            </div>
          )}
        </div>

        {/* Last sync + manual trigger */}
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            Laatste Synchronisatie
          </h3>

          {stats?.lastSync ? (
            <div className="flex flex-wrap gap-6 items-center mb-6">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Bestand</p>
                <p className="text-sm font-medium text-gray-900">
                  {stats.lastSync.blobName}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Status</p>
                {getSyncStatusChip(stats.lastSync.status)}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Voltooid op</p>
                <p className="text-sm text-gray-700">
                  {formatDateTime(stats.lastSync.completedAt)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Verwerkt</p>
                <p className="text-sm text-gray-700">
                  {stats.lastSync.recordsProcessed} records
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-6">
              Nog geen synchronisatie uitgevoerd.
            </p>
          )}

          {/* Manual sync trigger */}
          <div className="flex items-center gap-4">
            <Button
              label={syncing ? 'Synchroniseren...' : 'Synchroniseren'}
              size={ButtonSize.MEDIUM}
              radius={GlobalRadius.ROUNDED}
              border={Border.BORDER}
              onClick={handleSync}
            />
            {syncing && (
              <div className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span className="text-sm text-gray-500">
                  Azure blobs worden opgehaald...
                </span>
              </div>
            )}
          </div>

          {/* Sync result */}
          {syncResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800 mb-2">
                Synchronisatie voltooid
              </p>
              <div className="flex gap-6 text-sm text-green-700">
                <span>Verwerkt: <strong>{syncResult.blobsProcessed}</strong></span>
                <span>Overgeslagen: <strong>{syncResult.blobsSkipped}</strong></span>
                <span>Mislukt: <strong>{syncResult.blobsFailed}</strong></span>
              </div>
              {syncResult.details.length > 0 && (
                <div className="mt-3 space-y-1">
                  {syncResult.details.map((d, i) => (
                    <div key={i} className="text-xs text-green-700 flex gap-2">
                      <span className="font-mono">{d.blobName}</span>
                      <span>—</span>
                      <span>{d.status}</span>
                      {d.recordsInserted !== undefined && (
                        <span className="text-green-600">
                          +{d.recordsInserted} nieuw, ~{d.recordsUpdated ?? 0} bijgewerkt
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sync error */}
          {syncError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{syncError}</p>
            </div>
          )}
        </div>

        {/* Sync history table */}
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            Synchronisatiegeschiedenis
          </h3>

          {loadingHistory ? (
            <p className="text-gray-600">Laden...</p>
          ) : (
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={historyRows}
                columns={historyColumns}
                getRowId={(row) => row._rowId}
                pageSizeOptions={[10, 20, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 20 } },
                  sorting: {
                    sortModel: [{ field: 'startedAt', sort: 'desc' }],
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>
    </SimpleDashboardLayout>
  )
}

export default SyncDashboardPage
